import BetaOperations from "./BetaOperations";
import EndReviewPanel from "./EndReviewPanel";
import BetaConflictCheck from "./BetaConflictCheck";
import { usePortal } from "../Portal/PortalContext";
import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import {
  reviewApi,
  jurisdictionLabel,
  reviewStatusLabel,
  adminPaymentLabel,
  reviewNotStarted,
  dateLabel,
  dueLabel,
  isOverdue,
  nextActionLabel,
  nextActionInstruction,
  nextActionTone,
  ReviewSummary,
  topicLabel,
  shortDate,
} from "./api";
import "./reviews.css";
import { ArrowUpRight, FileText, RefreshCw, Search } from "lucide-react";
const attorneyTabs: [string, string, string?][] = [
  ["needs_you", "Needs you", "needs_you_count"],
  ["in_progress", "In progress", "in_progress_count"],
  ["waiting", "Waiting on others", "waiting_count"],
  ["completed", "Delivered", "delivered_count"],
  ["closed", "Closed"],
  ["all", "All"],
];
const filterLabel: Record<string, string> = {
  assigned: "newly assigned reviews",
  awaiting_agreement: "reviews awaiting agreement",
  closed: "closed reviews",
  cancelled: "withdrawn reviews",
  overdue: "overdue reviews",
  due_soon: "reviews due in the next 24 hours",
  needs_reply: "reviews that need your reply",
  awaiting_client: "reviews awaiting the client",
};
const attorneyEmptyTitle: Record<string, string> = {
  needs_you: "Nothing needs you right now",
  waiting: "You are not waiting on anyone",
  all: "No reviews assigned to you yet",
  assigned: "Nothing new to accept",
  needs_reply: "No client is waiting on you",
  awaiting_client: "You are not waiting on any client",
  awaiting_agreement: "No terms waiting for agreement",
  in_progress: "Nothing in progress",
  overdue: "Nothing is overdue",
  due_soon: "Nothing due in the next 24 hours",
  completed: "No delivered answers yet",
  closed: "No closed reviews",
  cancelled: "No withdrawn reviews",
};
export default function ReviewsPage({
  mode = "client",
}: {
  mode?: "client" | "attorney" | "admin";
}) {
  const { access } = usePortal();
  const [params, setParams] = useSearchParams();
  const requestedFilter = params.get("filter") || "all";
  const filter = [
    "all",
    "closed",
    "cancelled",
    "overdue",
    "due_soon",
    "assigned",
    "in_progress",
    "completed",
    "awaiting_agreement",
    ...(mode !== "client" ? ["needs_reply", "awaiting_client"] : []),
    ...(mode === "attorney" ? ["needs_you", "waiting"] : []),
    ...(mode === "admin" ? ["queued", "not_paid"] : []),
  ].includes(requestedFilter)
    ? requestedFilter
    : "all";
  const setFilter = (value: string) =>
    setParams(previous => {
      const nextParams = new URLSearchParams(previous);
      if (value === "all") nextParams.delete("filter");
      else nextParams.set("filter", value);
      return nextParams;
    });
  const [rows, setRows] = useState<ReviewSummary[]>([]),
    [stats, setStats] = useState<any>(null),
    [search, setSearch] = useState(""),
    [query, setQuery] = useState(""),
    [loading, setLoading] = useState(true),
    [error, setError] = useState(""),
    [next, setNext] = useState<number | null>(null),
    [revision, setRevision] = useState(0),
    [selected, setSelected] = useState<ReviewSummary | null>(null);
  const questionId = params.get("questionId");
  const sort =
    params.get("sort") || (mode === "attorney" ? "needs_you" : "recent");
  const url = `?sort=${sort}&mode=${mode}&filter=${filter}&search=${encodeURIComponent(query)}${questionId ? `&questionId=${encodeURIComponent(questionId)}` : ""}`;
  useEffect(() => {
    const c = new AbortController();
    setLoading(true);
    setError("");
    setRows([]);
    reviewApi(url, "GET", undefined, c.signal)
      .then(data => {
        setRows(data.reviews);
        setStats(data.stats);
        setNext(data.nextOffset);
      })
      .catch(e => {
        if (!c.signal.aborted) setError(e.message);
      })
      .finally(() => {
        if (!c.signal.aborted) setLoading(false);
      });
    return () => c.abort();
  }, [url, revision]);
  // Attorneys see new assignments and replies without a Refresh button.
  useEffect(() => {
    if (mode !== "attorney") return;
    const refresh = () => {
      if (document.visibilityState !== "visible") return;
      reviewApi(url)
        .then(data => {
          setRows(old =>
            old.length > data.reviews.length ? old : data.reviews
          );
          setStats(data.stats);
        })
        .catch(() => {});
    };
    const timer = window.setInterval(refresh, 30_000);
    window.addEventListener("focus", refresh);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", refresh);
    };
  }, [mode, url]);
  async function more() {
    if (next === null) return;
    setLoading(true);
    try {
      const data = await reviewApi(`${url}&offset=${next}`);
      setRows(
        old =>
          Array.from(
            new Map([...old, ...data.reviews].map(r => [r.id, r])).values()
          ) as ReviewSummary[]
      );
      setNext(data.nextOffset);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }
  const title =
    mode === "admin"
      ? "Review assignments"
      : mode === "attorney"
        ? "Attorney workspace"
        : "My attorney reviews";
  return (
    <main className="reviewed-documents review-page">
      <header className="reviewed-documents-heading">
        <div>
          <p className="reviewed-eyebrow">
            {mode === "admin"
              ? "OPERATIONS"
              : mode === "attorney"
                ? "ATTORNEY PORTAL"
                : "ATTORNEY REVIEW"}
          </p>
          <h1>{title}</h1>
          <p>
            {mode === "admin"
              ? "Match each request with an attorney whose approved expertise and jurisdiction fit the work."
              : mode === "attorney"
                ? "Review proposed terms, deliver your professional answer, and manage client follow-up."
                : "Track the agreed work, talk with your attorney, and keep their written answers in one place."}
          </p>
        </div>
        <div className="review-header-actions">
          {mode === "admin" ? (
            <Link className="btn btn-outline-dark" to="/admin/attorneys">
              Attorney applications
            </Link>
          ) : mode === "attorney" ? null : (
            <Link className="btn btn-primary" to="/questions">
              My questions
            </Link>
          )}
          {mode !== "attorney" && (
            <Button
              variant="light"
              onClick={() => setRevision(n => n + 1)}
              disabled={loading}
            >
              <RefreshCw size={13} /> Refresh
            </Button>
          )}
        </div>
      </header>
      {mode==="admin" && access?.targetedBeta && <BetaOperations revision={revision} />}
      {mode === "attorney" && (
        <>
          <nav className="attorney-queue-tabs" aria-label="Review views">
            {attorneyTabs.map(([value, label, count]) => (
              <button
                key={value}
                type="button"
                aria-pressed={filter === value}
                onClick={() => setFilter(value)}
              >
                {label}
                {count && stats && <span>{stats[count]}</span>}
              </button>
            ))}
          </nav>
          <p className="attorney-queue-guidance">
            Needs you includes new assignments, client replies, and overdue
            work. A review can appear in more than one view.
          </p>
          {filter !== "all" && !loading && !error && (
            <p className="attorney-queue-filtered">
              {rows.length} {rows.length === 1 ? "review" : "reviews"} shown
              {next !== null ? " so far" : ""} in{" "}
              {attorneyTabs.find(([value]) => value === filter)?.[1] ||
                filterLabel[filter] ||
                filter}
              .{" "}
              <button type="button" onClick={() => setFilter("all")}>
                Show all
              </button>
            </p>
          )}
        </>
      )}
      {stats && mode !== "attorney" && (
        <div className="review-stats">
          <div>
            <strong>{stats.active_count}</strong>Active reviews
          </div>
          <div>
            <strong>{stats.needs_reply_count}</strong>Need your reply
          </div>
          <div>
            <strong>{stats.completed_count}</strong>Reviews you delivered
          </div>
        </div>
      )}
      <div className="review-toolbar">
        <Form
          onSubmit={e => {
            e.preventDefault();
            setQuery(search);
          }}
          className="review-search"
        >
          <Form.Control
            aria-label="Search reviews"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search title or client"
          />
          <Button type="submit" variant="light">
            <Search size={14} /> Search
          </Button>
        </Form>
        {mode !== "attorney" && (
          <Form.Select
            aria-label="Filter reviews"
            value={filter}
            disabled={loading}
            onChange={e => setFilter(e.target.value)}
          >
            <option value="all">All reviews</option>
            {mode === "admin" && (
              <>
                <option value="queued">Awaiting assignment</option>
                <option value="not_paid">Not paid (no card saved)</option>
              </>
            )}
            <option value="assigned">Newly assigned</option>
            <option value="awaiting_agreement">Awaiting agreement</option>
            <option value="in_progress">In progress</option>
            <option value="completed">Delivered answers</option>
            <option value="closed">Closed reviews</option>
            <option value="cancelled">Withdrawn / unable to match</option>
            <option value="overdue">Overdue</option>
            <option value="due_soon">Due in the next 24 hours</option>
            {mode !== "client" && (
              <>
                <option value="needs_reply">Needs your reply</option>
                <option value="awaiting_client">Awaiting client</option>
              </>
            )}
          </Form.Select>
        )}
        <Form.Select
          aria-label="Sort reviews"
          value={sort}
          onChange={e =>
            setParams(previous => {
              const next = new URLSearchParams(previous);
              next.set("sort", e.target.value);
              return next;
            })
          }
        >
          {mode === "attorney" && (
            <option value="needs_you">Needs you first</option>
          )}
          <option value="urgency">Delivery deadline first</option>
          <option value="recent">Latest activity first</option>
        </Form.Select>
      </div>
      {error && <Alert variant="danger">{error}</Alert>}
      {loading && !rows.length ? (
        <p role="status">Loading reviews…</p>
      ) : !error && !rows.length ? (
        <section className="reviewed-empty">
          <h2>
            {mode === "attorney"
              ? attorneyEmptyTitle[filter] || "No reviews match this view"
              : "No reviews here yet"}
          </h2>
          <p>
            {mode === "client"
              ? "Open a question and choose Request review after an AI answer."
              : mode === "attorney"
                ? query
                  ? "Try a different search."
                  : "New assignments appear here and in Updates."
                : "New client submissions will appear here for manual assignment."}
          </p>
        </section>
      ) : (
        <div className="review-cards">
          <div className="review-list-labels" aria-hidden="true">
            <span>Review</span>
            <span>{mode === "client" ? "Attorney" : "Client"}</span>
            <span>Status</span>
            <span></span>
          </div>
          {rows.map(r => (
            <article className="review-card" key={r.id}>
              <div className="review-row-main">
                <span className="review-row-icon">
                  <FileText size={17} strokeWidth={1.5} />
                </span>
                <div>
                  <h2>
                    {mode === "admin" ? (
                      r.title
                    ) : (
                      <Link to={`/reviews/${r.id}`}>{r.title}</Link>
                    )}
                  </h2>
                  <p>
                    {topicLabel(r.topic_key)} ·{" "}
                    {jurisdictionLabel(
                      (mode !== "client" &&
                        r.assigned_coverage?.jurisdiction) ||
                        r.jurisdiction
                    )}
                  </p>
                  {mode === "attorney" && (
                    <small className="review-reference">
                      Review {r.id.slice(0, 8).toUpperCase()}
                    </small>
                  )}
                </div>
              </div>
              <div className="review-row-person">
                <strong>
                  {mode === "client"
                    ? r.attorney_name || "Awaiting assignment"
                    : r.client_name}
                </strong>
                <small>
                  {mode === "admin"
                    ? r.attorney_name
                      ? `Assigned to ${r.attorney_name}`
                      : "No attorney assigned"
                    : `Submitted ${shortDate(r.created_at)}`}
                </small>
              </div>
              <div className="review-row-status">
                {mode === "attorney" && r.next_action ? (
                  <span className={`review-badge next-${nextActionTone(r)}`}>
                    {nextActionLabel(r)}
                  </span>
                ) : (
                  <span className={`review-badge ${r.status}`}>
                    {reviewStatusLabel(r)}
                  </span>
                )}
                {mode === "admin" && <AdminPaymentNote review={r} />}
                {mode === "attorney" && (
                  <small className="review-row-next-action">
                    {nextActionInstruction(r)}
                  </small>
                )}
                {r.completed_at ? (
                  <small>Delivered {dateLabel(r.completed_at)}</small>
                ) : (
                  r.due_at && (
                    <small className={isOverdue(r) ? "review-overdue" : ""}>
                      {isOverdue(r) ? "Overdue · " : "Due "}
                      {mode === "attorney"
                        ? dueLabel(r.due_at)
                        : dateLabel(r.due_at)}
                    </small>
                  )
                )}
                {!r.closed_at &&
                  r.status !== "cancelled" &&
                  Number(r.latest_client_seq) >
                    Number(r.attorney_replied_through) &&
                  mode === "admin" && (
                    <span className="review-reply">Client needs a reply</span>
                  )}
              </div>
              <div className="review-row-action">
                {mode === "admin" ? (
                  <Button
                    variant="outline-dark"
                    aria-label={
                      r.assigned_attorney_id
                        ? "Manage assignment"
                        : "Assign attorney"
                    }
                    onClick={() => setSelected(r)}
                  >
                    {r.assigned_attorney_id ? "Manage" : "Assign"}
                    <ArrowUpRight size={13} aria-hidden="true" />
                  </Button>
                ) : (
                  <Link to={`/reviews/${r.id}`}>
                    Open review <ArrowUpRight size={13} />
                  </Link>
                )}
              </div>
            </article>
          ))}
        </div>
      )}

      {next !== null && (
        <Button
          disabled={loading}
          className="mt-4"
          variant="light"
          onClick={more}
        >
          Load more
        </Button>
      )}
      {selected && (
        <AssignmentModal
          review={selected}
          onHide={() => setSelected(null)}
          onAssigned={() => {
            setSelected(null);
            setRevision(n => n + 1);
          }}
        />
      )}
    </main>
  );
}
function AssignmentModal({
  review,
  onHide,
  onAssigned,
}: {
  review: ReviewSummary;
  onHide: () => void;
  onAssigned: () => void;
}) {
  const { access } = usePortal();
  const [assignmentVersion,setAssignmentVersion] = useState(review.version);
  const [attorneys, setAttorneys] = useState<any[]>([]),
    [events, setEvents] = useState<any[]>([]),
    [selected, setSelected] = useState(""),
    [reason, setReason] = useState(""),
    [busy, setBusy] = useState(false),
    [loading, setLoading] = useState(true),
    [error, setError] = useState("");
  useEffect(() => {
    const c = new AbortController();
    Promise.all([
      reviewApi("/attorneys", "GET", undefined, c.signal),
      reviewApi(`/${review.id}/events`, "GET", undefined, c.signal),
    ])
      .then(([a, e]) => {
        setAttorneys(a.attorneys);
        setEvents(e.events);
      })
      .catch(e => {
        if (!c.signal.aborted) setError(e.message);
      })
      .finally(() => {
        if (!c.signal.aborted) setLoading(false);
      });
    return () => c.abort();
  }, [review.id]);
  const candidates = attorneys
    .filter(a => a.status === "approved" && a.available)
    .flatMap(a => a.coverage.map((c: any, index: number) => ({ a, c, index })))
    .filter(
      ({ c }) =>
        c.topicKey === review.topic_key &&
        (review.jurisdiction.level === "unsure" ||
          (c.jurisdiction.level === review.jurisdiction.level &&
            c.jurisdiction.countryCode === review.jurisdiction.countryCode &&
            (c.jurisdiction.level !== "state_region" ||
              c.jurisdiction.region.toLowerCase().trim() ===
                review.jurisdiction.region.toLowerCase().trim())))
    );
  async function assign() {
    setBusy(true);
    setError("");
    try {
      const [attorneyId, index] = selected.split(":");
      await reviewApi(`/${review.id}/assign`, "POST", {
        attorneyId,
        coverageIndex: Number(index),
        version: assignmentVersion,
        reason,
      });
      onAssigned();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal show onHide={() => !busy && onHide()} size="lg" centered>
      <Modal.Header closeButton={!busy}>
        <Modal.Title>Manual attorney assignment</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <h2 className="h5">{review.title}</h2>
        <p>
          {review.topic_key} · {jurisdictionLabel(review.jurisdiction)}
        </p>
        {error && <Alert variant="danger">{error}</Alert>}
        {loading ? (
          <p>Loading eligible attorneys…</p>
        ) : (
          <>
            {!candidates.length && (
              <Alert variant="info">
                No approved, available attorney matches this question. Review
                applications and coverage first.
              </Alert>
            )}
            {review.jurisdiction.level === "unsure" && (
              <Alert variant="info">
                The client has not selected a jurisdiction. Choose the routing
                coverage for this assignment and explain why below. The original
                intake stays in the submitted record.
              </Alert>
            )}
            <Form.Label htmlFor="assign-attorney">
              Eligible attorney and approved coverage
            </Form.Label>
            <Form.Select
              id="assign-attorney"
              value={selected}
              onChange={e => setSelected(e.target.value)}
            >
              <option value="">Choose an attorney</option>
              {candidates.map(({ a, c, index }) => (
                <option
                  key={`${a.user_id}:${index}`}
                  value={`${a.user_id}:${index}`}
                >
                  {a.display_name} · {jurisdictionLabel(c.jurisdiction)} ·{" "}
                  {a.experience_years} years reported · {a.active_count} active
                  / {a.completed_count} delivered
                </option>
              ))}
            </Form.Select>
            {access?.targetedBeta && <BetaConflictCheck reviewId={review.id} attorneyId={selected.split(":")[0]} onChecked={setAssignmentVersion} />}
            <Form.Label className="mt-3" htmlFor="assign-reason">
              Assignment or reassignment reason
            </Form.Label>
            <Form.Control
              id="assign-reason"
              as="textarea"
              rows={3}
              maxLength={2000}
              value={reason}
              onChange={e => setReason(e.target.value)}
            />
          </>
        )}
        <EndReviewPanel id={review.id} onEnded={onAssigned} />
        <details className="mt-4">
          <summary>Assignment and status history</summary>
          {events.map((e, i) => (
            <p className="mt-2" key={i}>
              {e.kind.replaceAll("_", " ")} · {e.actor_name} ·{" "}
              {dateLabel(e.created_at)}
              {e.metadata.reason && (
                <>
                  <br />
                  {e.metadata.reason}
                </>
              )}
            </p>
          ))}
        </details>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" disabled={busy} onClick={onHide}>
          Close
        </Button>
        {reviewNotStarted(review) && (
          <Button
            variant="outline-danger"
            disabled={busy || !reason.trim()}
            onClick={async () => {
              if (
                !window.confirm(
                  "End this request as unable to match? The client will receive the reason above."
                )
              )
                return;
              setBusy(true);
              try {
                await reviewApi(`/${review.id}/unable-to-match`, "POST", {
                  version: assignmentVersion,
                  reason,
                });
                onAssigned();
              } catch (e: any) {
                setError(e.message);
              } finally {
                setBusy(false);
              }
            }}
          >
            Unable to match
          </Button>
        )}
        <Button
          disabled={
            busy ||
            !selected ||
            !reason.trim() ||
            !!review.closed_at ||
            review.status === "cancelled"
          }
          onClick={assign}
        >
          {busy ? "Assigning…" : "Save assignment"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

/** Payment standing on an admin queue row; unpaid requests stand out. */
function AdminPaymentNote({ review }: { review: ReviewSummary }) {
  const payment = adminPaymentLabel(review);
  if (!payment) return null;
  return (
    <small className={payment.unpaid ? "review-payment-unpaid" : undefined}>
      {payment.text}
    </small>
  );
}
